import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';

const TermsOfService: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      <div className="max-w-4xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {isKorean ? '서비스 이용약관' : 'Terms of Service'}
        </h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-6">
            {isKorean 
              ? '최종 업데이트: 2025년 8월 1일'
              : 'Last Updated: August 1, 2025'}
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '1. 서비스 약관 동의' : '1. Acceptance of Terms'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? 'AivueBiz Desktop("서비스")을 이용함으로써 귀하는 본 이용약관에 동의하게 됩니다. 본 약관에 동의하지 않으시면 서비스를 이용하지 마십시오.'
                : 'By using AivueBiz Desktop ("Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '2. 서비스 설명' : '2. Description of Service'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? 'AivueBiz는 실시간 번역, 화상 통화, 채팅 및 AI 음성 인식 기능을 제공하는 글로벌 비즈니스 커뮤니케이션 플랫폼입니다. 우리는 macOS 및 Windows용 데스크톱 애플리케이션을 제공합니다.'
                : 'AivueBiz is a global business communication platform providing real-time translation, video calls, chat, and AI speech recognition features. We provide desktop applications for macOS and Windows.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '3. 구독 및 결제' : '3. Subscriptions and Payment'}
            </h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '무료 플랜: 기본 기능에 대한 제한적 접근'
                  : 'Free Plan: Limited access to basic features'}
              </li>
              <li>
                {isKorean
                  ? '개인 플랜 ($20.00/월): 개인 전문가용 확장 기능'
                  : 'Personal Plan ($20.00/month): Extended features for individual professionals'}
              </li>
              <li>
                {isKorean
                  ? '비즈니스 플랜 ($40.00/월): 팀 협업 기능'
                  : 'Business Plan ($40.00/month): Team collaboration features'}
              </li>
              <li>
                {isKorean
                  ? '엔터프라이즈 플랜 ($60.00/월): 대규모 조직을 위한 전체 기능'
                  : 'Enterprise Plan ($60.00/month): Full features for large organizations'}
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              {isKorean
                ? '결제는 LemonSqueezy를 통해 안전하게 처리됩니다. 구독은 취소할 때까지 자동으로 갱신됩니다.'
                : 'Payments are processed securely through LemonSqueezy. Subscriptions will automatically renew until cancelled.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '4. 사용자 계정' : '4. User Accounts'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '서비스를 이용하려면 계정을 생성해야 합니다. 귀하는 계정 정보의 기밀성을 유지하고 계정에서 발생하는 모든 활동에 대해 책임을 집니다.'
                : 'You must create an account to use the Service. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '5. 허용된 사용' : '5. Acceptable Use'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '귀하는 서비스를 합법적인 목적으로만 사용하는 데 동의합니다. 다음은 금지됩니다:'
                : 'You agree to use the Service only for lawful purposes. You may not:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>{isKorean ? '불법 콘텐츠 전송' : 'Transmit illegal content'}</li>
              <li>{isKorean ? '다른 사용자 괴롭히기 또는 학대' : 'Harass or abuse other users'}</li>
              <li>{isKorean ? '서비스의 보안 침해 시도' : 'Attempt to breach the security of the Service'}</li>
              <li>{isKorean ? '지적 재산권 침해' : 'Infringe intellectual property rights'}</li>
              <li>{isKorean ? '스팸 또는 원치 않는 콘텐츠 배포' : 'Distribute spam or unwanted content'}</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '6. 데이터 보존 및 저장' : '6. Data Retention and Storage'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '데이터 보존 기간은 구독 플랜에 따라 다릅니다. 무료 플랜은 1개월, 개인 플랜은 3개월, 비즈니스 플랜은 6개월, 엔터프라이즈 플랜은 맞춤형 보존 기간을 제공합니다.'
                : 'Data retention periods vary by subscription plan. Free plan offers 1 month, Personal plan 3 months, Business plan 6 months, and Enterprise plan offers custom retention periods.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '7. 지적 재산권' : '7. Intellectual Property'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '서비스와 그 콘텐츠는 Groove Inc.의 소유이며 저작권 및 기타 법률에 의해 보호됩니다. 귀하가 생성한 콘텐츠에 대한 권리는 귀하가 보유합니다.'
                : 'The Service and its content are owned by Groove Inc. and protected by copyright and other laws. You retain rights to content you create.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '8. 서비스 종료' : '8. Termination'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '귀하는 언제든지 계정을 취소할 수 있습니다. 우리는 약관 위반 시 서비스 접근을 종료할 권리를 보유합니다.'
                : 'You may cancel your account at any time. We reserve the right to terminate access to the Service for violations of these terms.'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '구독 취소: 현재 결제 기간 종료까지 서비스 이용 가능'
                  : 'Subscription cancellation: Service remains available until the end of current billing period'}
              </li>
              <li>
                {isKorean
                  ? '취소 후 자동으로 무료 플랜으로 전환'
                  : 'Automatically converts to Free plan after cancellation'}
              </li>
              <li>
                {isKorean
                  ? '데이터는 플랜별 보존 기간에 따라 유지'
                  : 'Data retained according to plan retention periods'}
              </li>
              <li>
                {isKorean
                  ? '언제든지 재구독 가능'
                  : 'Re-subscription available at any time'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '9. 책임 제한' : '9. Limitation of Liability'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '서비스는 "있는 그대로" 제공됩니다. 법이 허용하는 최대 범위 내에서, 우리는 서비스 사용으로 인한 간접적, 우발적, 특별, 결과적 또는 징벌적 손해에 대해 책임지지 않습니다.'
                : 'The Service is provided "as is". To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '10. 약관 변경' : '10. Changes to Terms'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 언제든지 이 약관을 수정할 권리를 보유합니다. 변경 사항은 웹사이트에 게시되며, 계속 사용하면 수정된 약관에 동의하는 것으로 간주됩니다.'
                : 'We reserve the right to modify these terms at any time. Changes will be posted on our website, and continued use constitutes acceptance of modified terms.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '11. 사용량 관리 및 현지 시간 기준' : '11. Usage Management and Local Time Standards'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '서비스 사용량 제한 및 일일 한도는 사용자의 현지 시간대를 기준으로 계산됩니다:'
                : 'Service usage limits and daily quotas are calculated based on your local timezone:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '일일 사용량은 사용자의 현지 시간 자정(00:00)에 초기화됩니다'
                  : 'Daily usage resets at midnight (00:00) in your local time'}
              </li>
              <li>
                {isKorean
                  ? '메시지, 영상통화, AI 음성인식, 파일 전송 사용량이 현지 시간 기준으로 추적됩니다'
                  : 'Messages, video calls, AI speech recognition, and file transfers are tracked based on local time'}
              </li>
              <li>
                {isKorean
                  ? '글로벌 사용자를 위해 20개 이상의 주요 시간대를 자동으로 감지합니다'
                  : 'Automatically detects over 20 major timezones for global users'}
              </li>
            </ul>
            
            <h3 className="text-xl font-semibold mb-3 mt-4">
              {isKorean ? '11.1 플랜 변경 및 유예 기간' : '11.1 Plan Changes and Grace Periods'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '플랜 변경 시 다음 정책이 적용됩니다:'
                : 'The following policies apply to plan changes:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                <strong>{isKorean ? '업그레이드:' : 'Upgrades:'}</strong> {isKorean
                  ? '즉시 적용되며 새로운 기능을 바로 사용할 수 있습니다'
                  : 'Applied immediately with instant access to new features'}
              </li>
              <li>
                <strong>{isKorean ? '다운그레이드:' : 'Downgrades:'}</strong> {isKorean
                  ? '다음 결제일부터 적용되며, 그 때까지 현재 플랜의 모든 기능을 계속 사용할 수 있습니다'
                  : 'Takes effect from the next billing date, until then you can continue using all current plan features'}
              </li>
              <li>
                <strong>{isKorean ? '실시간 동기화:' : 'Real-time Sync:'}</strong> {isKorean
                  ? '플랜 변경은 실시간으로 감지되며, 앱 내 알림으로 안내됩니다'
                  : 'Plan changes are detected in real-time with in-app notifications'}
              </li>
              <li>
                <strong>{isKorean ? '사용량 추적:' : 'Usage Tracking:'}</strong> {isKorean
                  ? '플랜 변경 후에도 사용량은 정확하게 추적되며, 다운그레이드 예약 시 현재 플랜의 한도가 계속 적용됩니다'
                  : 'Usage is accurately tracked after plan changes, with current plan limits continuing for scheduled downgrades'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '12. 법적 및 규제 준수' : '12. Legal and Regulatory Compliance'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '우리는 글로벌 데이터 보호 및 소비자 보호 법규를 준수합니다:'
                : 'We comply with global data protection and consumer protection regulations:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                <strong>{isKorean ? 'GDPR (유럽)' : 'GDPR (Europe)'}</strong>: {isKorean
                  ? '유럽 사용자의 데이터는 현지 시간 기준으로 처리되며, 사용자의 권리가 보호됩니다'
                  : 'European users\' data is processed based on local time standards with full protection of user rights'}
              </li>
              <li>
                <strong>{isKorean ? '각국 소비자 보호법' : 'National Consumer Protection Laws'}</strong>: {isKorean
                  ? '모든 서비스 제한 및 과금은 사용자의 현지 시간대를 기준으로 공정하게 적용됩니다'
                  : 'All service limits and billing are fairly applied based on users\' local timezone'}
              </li>
              <li>
                <strong>{isKorean ? '데이터 현지화' : 'Data Localization'}</strong>: {isKorean
                  ? '사용자의 사용량 데이터는 현지 법규에 따라 처리 및 저장됩니다'
                  : 'User usage data is processed and stored in compliance with local regulations'}
              </li>
              <li>
                <strong>{isKorean ? '투명성' : 'Transparency'}</strong>: {isKorean
                  ? '사용량 추적 방식과 시간대 적용 기준을 명확하게 공개합니다'
                  : 'Clear disclosure of usage tracking methods and timezone application standards'}
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              {isKorean
                ? '이러한 현지 시간 기준 사용량 관리는 공정한 서비스 제공과 법적 준수를 보장합니다.'
                : 'This local time-based usage management ensures fair service provision and legal compliance.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '13. 준거법' : '13. Governing Law'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '본 약관은 대한민국 법률에 따라 규율됩니다.'
                : 'These terms shall be governed by the laws of the Republic of Korea.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '14. 연락처' : '14. Contact Information'}
            </h2>
            <p className="text-gray-700">
              {isKorean
                ? '질문이 있으시면 support@aivuebiz.com으로 문의하세요.'
                : 'For questions about these terms, please contact us at support@aivuebiz.com.'}
            </p>
            <p className="text-gray-700 mt-2">
              Groove Inc.<br />
              Seoul, Republic of Korea<br />
              Email: support@aivuebiz.com
            </p>
          </section>
        </div>
      </div>
      
      <Footer t={t} />
    </div>
  );
};

export default TermsOfService;